/**
 * MarketingHome — W55 Marketing Conversion Dashboard.
 *
 * Replaces the old sprint-board / assist-queue / credit / hooks tab panel
 * with a conversion-diagnostic dashboard per Bryan's method:
 *   - MQL pacing vs quarterly target
 *   - Pipeline Reality strip (WON/COMMIT/PROBABLE/UPSIDE/OMITTED)
 *   - Today's Drivers: Touches / Engagements / MQLs Created sparklines
 *   - Inverted funnel: Touches → Engagements → MQLs → Accepted → Meetings → Opps
 *   - Channel Mix + MQL Reconciliation
 *   - Active Hook Campaigns + Upcoming Events
 *   - MQL Plan 2026 (quarterly + monthly)
 *   - MQL → SQL Conversion Waterfall
 *   - BDR Handoff Queue
 *
 * Visual canon: 5-color palette (teal #0F766E, blue #0B0B0F, purple #7C3AED,
 * dark-red #7F1D1D, light-orange #F97316) + shadcn/ui + Tailwind.
 * W53 Manager Dashboard density (tight padding).
 * No exclamation points. No emojis. M-PACT hyphenated.
 *
 * Tenant 1 only. SIM/SMOKE_TEST excluded by all child components.
 *
 * W55 — 2026-05-29
 */

import { useState, useEffect } from "react";
import { api } from "@/lib/session";
import OperatorShell from "@/components/operator/OperatorShell";
import { MqlPacingBar } from "@/components/marketing/MqlPacingBar";
import { PipelineRealityStrip } from "@/components/marketing/PipelineRealityStrip";
import { ActivityTiles } from "@/components/marketing/ActivityTiles";
import { FunnelChart } from "@/components/marketing/FunnelChart";
import { ChannelMix } from "@/components/marketing/ChannelMix";
import { MqlReconciliation } from "@/components/marketing/MqlReconciliation";
import { ActiveHooks } from "@/components/marketing/ActiveHooks";
import { UpcomingEvents } from "@/components/marketing/UpcomingEvents";
import { MqlPlanTable } from "@/components/marketing/MqlPlanTable";
import { ConversionWaterfall } from "@/components/marketing/ConversionWaterfall";
import { BdrHandoffQueue } from "@/components/marketing/BdrHandoffQueue";

// ─── W66.H: Funnel Banner ───────────────────────────────────────────────────────────────

const FUNNEL_STAGE_COLORS: Record<string, string> = {
  MQL: "#6B7280",
  SQL: "#EAB308",
  Opportunity: "#16A34A",
  Won: "#16A34A",
};
const FUNNEL_TEXT_COLORS: Record<string, string> = {
  MQL: "#FFFFFF",
  SQL: "#1C1917",
  Opportunity: "#FFFFFF",
  Won: "#FFFFFF",
};

interface FunnelStageData {
  key: string;
  label: string;
  count: number;
}

function FunnelBanner() {
  const [stages, setStages] = useState<FunnelStageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api<{ period: { label: string }; stages: FunnelStageData[] }>("/api/funnel?period=current_quarter")
      .then((data) => {
        if (cancelled) return;
        setStages(data.stages ?? []);
        setLabel(data.period?.label ?? "");
      })
      .catch(() => {
        // graceful fail — pills remain in loading state but do not error
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      style={{
        background: "#F9FAFB",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: "10px 14px",
        marginBottom: 20,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 10,
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9CA3AF", whiteSpace: "nowrap" }}>
        Funnel
      </span>
      {loading ? (
        ["MQL", "SQL", "Opportunity", "Won"].map((k) => (
          <span
            key={k}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 10px",
              borderRadius: 14,
              background: "#E5E7EB",
              fontSize: 12,
              color: "#9CA3AF",
              fontWeight: 600,
            }}
          >
            {k}&nbsp;&mdash;
          </span>
        ))
      ) : (
        stages.map((stage) => (
          <a
            key={stage.key}
            href="#/funnel"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 10px",
              borderRadius: 14,
              background: FUNNEL_STAGE_COLORS[stage.key] ?? "#6B7280",
              color: FUNNEL_TEXT_COLORS[stage.key] ?? "#FFFFFF",
              fontSize: 12,
              fontWeight: 700,
              textDecoration: "none",
            }}
            aria-label={`${stage.label}: ${stage.count} accounts — open funnel`}
          >
            {stage.label}
            <span
              style={{
                background: "rgba(255,255,255,0.22)",
                borderRadius: 10,
                padding: "0 5px",
                fontSize: 11,
                fontWeight: 800,
              }}
            >
              {stage.count}
            </span>
          </a>
        ))
      )}
      {label && (
        <span style={{ fontSize: 10, color: "#D1D5DB", marginLeft: 4 }}>{label}</span>
      )}
      <a
        href="#/funnel"
        style={{
          marginLeft: "auto",
          fontSize: 11,
          color: "#0F766E",
          fontWeight: 600,
          textDecoration: "none",
          whiteSpace: "nowrap",
        }}
      >
        View full funnel →
      </a>
    </div>
  );
}

// ─── Secondary nav links ──────────────────────────────────────────────────────
const NAV_LINKS = [
  { label: "Marketing plan", href: "#/marketing/plan" },
  { label: "Calendar", href: "#/marketing/calendar" },
  { label: "Hooks", href: "#/marketing/hooks" },
  { label: "Activity log", href: "#/log/marketing" },
  { label: "Dashboard", href: "#/dashboards/marketing" },
] as const;

// ─── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="mb-3">
      <h2 className="text-[13px] font-bold uppercase tracking-widest" style={{ color: "#0B0B0F" }}>
        {title}
      </h2>
      {sub && <p className="mt-0.5 text-[11px] text-neutral-400">{sub}</p>}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function MarketingHome() {
  return (
    <OperatorShell
      breadcrumbs={[{ label: "Marketing" }]}
      pageTitle="Marketing"
      pageMeta="Conversion diagnostic dashboard — M-PACT method"
    >
      <div className="mx-auto max-w-6xl px-4 py-5">

        {/* ── Page title ──────────────────────────────────────────────────── */}
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-xl font-semibold"
              style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}
            >
              Marketing
            </h1>
            <p className="mt-0.5 text-sm text-neutral-500">
              Conversion-diagnostic dashboard &middot; MQL pacing, funnel sharpness, BDR handoff.
            </p>
          </div>
          {/* Quick nav links */}
          <div className="hidden sm:flex items-center gap-3 flex-wrap justify-end">
            {NAV_LINKS.map(l => (
              <a
                key={l.href}
                href={l.href}
                className="text-[11px] text-neutral-500 hover:text-[#DC2626] hover:underline"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>

        {/* ── W66.H: Real Funnel Banner (replaces em-dash placeholders) ──────────── */}
        <FunnelBanner />

        {/* ═══════════════════════════════════════════════════════════════════
            HERO BAND — MQL pacing + Pipeline Reality + Activity tiles
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="mb-8 space-y-4">
          {/* Row 1: MQL pacing (wider) + Pipeline Reality */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <MqlPacingBar />
            <PipelineRealityStrip />
          </div>

          {/* Row 2: Today's Drivers */}
          <div>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-neutral-400">
              Today&apos;s Drivers &middot; Activity
            </div>
            <ActivityTiles />
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            MONEY SHOT — Inverted Funnel
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="mb-8">
          <SectionHeader
            title="Conversion Funnel"
            sub="Stage volumes and conversion rates — Touches through Opportunities."
          />
          <div className="rounded-lg border border-neutral-200 bg-white p-5">
            <FunnelChart />
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            BELOW THE FOLD ROW 1 — Channel Mix + MQL Reconciliation
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="mb-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <ChannelMix />
          <MqlReconciliation />
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            BELOW THE FOLD ROW 2 — Active Hooks + Upcoming Events
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="mb-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <ActiveHooks />
          <UpcomingEvents />
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            FULL-WIDTH ROW 3 — MQL Plan 2026
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="mb-8">
          <MqlPlanTable />
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            FULL-WIDTH ROW 4 — Conversion Waterfall
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="mb-8">
          <ConversionWaterfall />
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            FULL-WIDTH ROW 5 — BDR Handoff Queue
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="mb-8">
          <BdrHandoffQueue />
        </section>

      </div>
    </OperatorShell>
  );
}
